import type { QueryClient } from '@tanstack/react-query';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type {
  ContributorFlowPlaylistScope,
  EventMediaSettingsDto,
  FlowMediaSettingsDto,
  SessionMediaSettingsDto,
} from '@/types/dtos/media/media-settings.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  buildFlowEffectiveMediaSettings,
  buildFlowMediaSettingsCapabilities,
  defaultEventMediaSettings,
} from '@/helpers/flow-media-settings-resolver.helper';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_SETTINGS_QUERY_KEY } from '@/state/queries/events/use-get-event-settings';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { FLOW_PARENT_ENTITY } from '@/types/dtos/flows/response/flow-parent-entity.enum';

function flowMatchesOriginId(flow: FlowDto, flowOriginId: string): boolean {
  const origin = flow.origin ? getObjectId(flow.origin) : flow.id;
  return flow.id === flowOriginId || origin === flowOriginId;
}

function sessionMatchesOriginId(session: SessionDto, sessionOriginId: string): boolean {
  const origin = session.origin ? getObjectId(session.origin) : session.id;
  return session.id === sessionOriginId || origin === sessionOriginId;
}

function resolveFlowEventOriginId(flow: FlowDto): string | undefined {
  if (flow.parentType !== FLOW_PARENT_ENTITY.EVENT) {
    return undefined;
  }
  const parent = flow.parent;
  return 'origin' in parent && parent.origin ? getObjectId(parent.origin) : getObjectId(parent);
}

function findMatchingFlow(data: FlowDto | FlowDto[], flowOriginId: string): FlowDto | undefined {
  const flows = Array.isArray(data) ? data : [data];
  return flows.find((flow) => flowMatchesOriginId(flow, flowOriginId));
}

export function resolveFlowEventOriginIdFromCache(queryClient: QueryClient, flowOriginId: string): string | undefined {
  for (const [, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({ queryKey: [GET_FLOW_QUERY_KEY] })) {
    if (!data) continue;
    const matchingFlow = findMatchingFlow(data, flowOriginId);
    if (matchingFlow) {
      return resolveFlowEventOriginId(matchingFlow);
    }
  }

  for (const [, data] of queryClient.getQueriesData<FlowDto[]>({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] })) {
    if (!data) continue;
    const matchingFlow = findMatchingFlow(data, flowOriginId);
    if (matchingFlow) {
      return resolveFlowEventOriginId(matchingFlow);
    }
  }

  const flowStore = useFlowStore.getState();
  for (const flow of [flowStore.flow, flowStore.flowPlan, flowStore.flowOrganize]) {
    if (flow && flowMatchesOriginId(flow, flowOriginId)) {
      return resolveFlowEventOriginId(flow);
    }
  }

  return undefined;
}

export function resolveEventMediaSettingsFromCache(
  queryClient: QueryClient,
  eventOriginId: string | undefined,
): EventMediaSettingsDto {
  if (!eventOriginId) {
    return defaultEventMediaSettings();
  }
  const settings = queryClient.getQueryData<GetEventSettingsDto>([GET_EVENT_SETTINGS_QUERY_KEY, eventOriginId]);
  return settings?.mediaSettings ?? settings?.effectiveMediaSettings ?? defaultEventMediaSettings();
}

/** PATCH returns merged settings from the API but may omit empty branches; never drop existing hosts/speakers. */
export function mergeFlowMediaSettingsDto(
  existing: FlowMediaSettingsDto | undefined,
  incoming: FlowMediaSettingsDto,
): FlowMediaSettingsDto {
  const merged: FlowMediaSettingsDto = {
    ...existing,
    ...incoming,
  };

  if (incoming.hosts !== undefined) {
    merged.hosts = { ...existing?.hosts, ...incoming.hosts };
    if (incoming.hosts.scopes !== undefined) {
      const mergedScopes = { ...(existing?.hosts?.scopes ?? {}) };
      for (const [scope, scopePatch] of Object.entries(incoming.hosts.scopes)) {
        if (scopePatch !== undefined) {
          mergedScopes[scope as ContributorFlowPlaylistScope] = {
            ...(mergedScopes[scope as ContributorFlowPlaylistScope] ?? {}),
            ...scopePatch,
          };
        }
      }
      merged.hosts = { ...merged.hosts, scopes: mergedScopes };
    }
  }

  if (incoming.speakers !== undefined) {
    merged.speakers = { ...existing?.speakers, ...incoming.speakers };
  }

  if (incoming.virtualMedia !== undefined) {
    merged.virtualMedia = { ...existing?.virtualMedia, ...incoming.virtualMedia };
  }

  return merged;
}

function applyFlowMediaSettingsPatch(
  flow: FlowDto,
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventSettings: EventMediaSettingsDto,
): FlowDto {
  if (!flowMatchesOriginId(flow, flowOriginId)) {
    return flow;
  }

  const mergedMediaSettings = mergeFlowMediaSettingsDto(flow.mediaSettings, mediaSettings);

  return {
    ...flow,
    mediaSettings: mergedMediaSettings,
    effectiveMediaSettings: buildFlowEffectiveMediaSettings(eventSettings, mergedMediaSettings),
    mediaSettingsCapabilities: buildFlowMediaSettingsCapabilities(
      eventSettings,
      mergedMediaSettings,
      flow.mediaSettingsCapabilities,
    ),
  };
}

function patchSessionEntity(
  session: SessionDto,
  sessionOriginId: string,
  mediaSettings: SessionMediaSettingsDto,
): SessionDto {
  return sessionMatchesOriginId(session, sessionOriginId) ? { ...session, mediaSettings } : session;
}

function patchFlowQueryData(
  data: FlowDto | FlowDto[],
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventSettings: EventMediaSettingsDto,
): FlowDto | FlowDto[] {
  if (Array.isArray(data)) {
    return data.map((flow) => applyFlowMediaSettingsPatch(flow, flowOriginId, mediaSettings, eventSettings));
  }
  return applyFlowMediaSettingsPatch(data, flowOriginId, mediaSettings, eventSettings);
}

function patchFlowListInEventStore(
  flows: FlowDto[] | undefined,
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventSettings: EventMediaSettingsDto,
): FlowDto[] | undefined {
  if (!flows) {
    return flows;
  }
  let changed = false;
  const next = flows.map((flow) => {
    const patched = applyFlowMediaSettingsPatch(flow, flowOriginId, mediaSettings, eventSettings);
    if (patched !== flow) {
      changed = true;
    }
    return patched;
  });
  return changed ? next : flows;
}

export function patchFlowMediaSettingsInQueryCache(
  queryClient: QueryClient,
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventSettings?: EventMediaSettingsDto,
) {
  const eventOriginId = resolveFlowEventOriginIdFromCache(queryClient, flowOriginId);
  const resolvedEventSettings = eventSettings ?? resolveEventMediaSettingsFromCache(queryClient, eventOriginId);

  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({
    queryKey: [GET_FLOW_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchFlowQueryData(data, flowOriginId, mediaSettings, resolvedEventSettings));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto[]>({
    queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((flow) => applyFlowMediaSettingsPatch(flow, flowOriginId, mediaSettings, resolvedEventSettings)),
    );
  }
}

export function patchFlowMediaSettingsInFlowStore(
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventSettings?: EventMediaSettingsDto,
) {
  const resolvedEventSettings = eventSettings ?? defaultEventMediaSettings();

  const apply = (flow?: FlowDto) => {
    if (!flow || !flowMatchesOriginId(flow, flowOriginId)) return flow;
    return applyFlowMediaSettingsPatch(flow, flowOriginId, mediaSettings, resolvedEventSettings);
  };

  const state = useFlowStore.getState();
  const next = {
    flow: apply(state.flow),
    flowPlan: apply(state.flowPlan),
    flowOrganize: apply(state.flowOrganize),
  };

  if (next.flow !== state.flow || next.flowPlan !== state.flowPlan || next.flowOrganize !== state.flowOrganize) {
    useFlowStore.setState(next);
  }
}

export function patchFlowMediaSettingsInEventStore(
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventSettings?: EventMediaSettingsDto,
) {
  const resolvedEventSettings = eventSettings ?? defaultEventMediaSettings();
  const state = useEventStore.getState();

  const eventFlows = patchFlowListInEventStore(state.eventFlows, flowOriginId, mediaSettings, resolvedEventSettings);
  const eventFlowsPlan = patchFlowListInEventStore(
    state.eventFlowsPlan,
    flowOriginId,
    mediaSettings,
    resolvedEventSettings,
  );
  const eventFlowsOrganize = patchFlowListInEventStore(
    state.eventFlowsOrganize,
    flowOriginId,
    mediaSettings,
    resolvedEventSettings,
  );

  if (
    eventFlows !== state.eventFlows ||
    eventFlowsPlan !== state.eventFlowsPlan ||
    eventFlowsOrganize !== state.eventFlowsOrganize
  ) {
    useEventStore.setState({
      ...(eventFlows !== state.eventFlows ? { eventFlows } : {}),
      ...(eventFlowsPlan !== state.eventFlowsPlan ? { eventFlowsPlan } : {}),
      ...(eventFlowsOrganize !== state.eventFlowsOrganize ? { eventFlowsOrganize } : {}),
    });
  }
}

export function patchFlowMediaSettingsEverywhere(
  queryClient: QueryClient,
  flowOriginId: string,
  mediaSettings: FlowMediaSettingsDto,
  eventOriginId?: string,
) {
  const owningEventOriginId = eventOriginId ?? resolveFlowEventOriginIdFromCache(queryClient, flowOriginId);
  const eventSettings = resolveEventMediaSettingsFromCache(queryClient, owningEventOriginId);
  patchFlowMediaSettingsInFlowStore(flowOriginId, mediaSettings, eventSettings);
  patchFlowMediaSettingsInEventStore(flowOriginId, mediaSettings, eventSettings);
  patchFlowMediaSettingsInQueryCache(queryClient, flowOriginId, mediaSettings, eventSettings);
}

export function patchSessionMediaSettingsInQueryCache(
  queryClient: QueryClient,
  sessionOriginId: string,
  mediaSettings: SessionMediaSettingsDto,
) {
  for (const [queryKey, data] of queryClient.getQueriesData<SessionDto>({
    queryKey: [GET_SESSION_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchSessionEntity(data, sessionOriginId, mediaSettings));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<SessionDto[]>({
    queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((session) => patchSessionEntity(session, sessionOriginId, mediaSettings)),
    );
  }
}

export function patchSessionMediaSettingsInEventStore(sessionOriginId: string, mediaSettings: SessionMediaSettingsDto) {
  const patchList = (sessions?: SessionDto[]) => {
    if (!sessions) return sessions;
    let changed = false;
    const next = sessions.map((session) => {
      if (!sessionMatchesOriginId(session, sessionOriginId)) return session;
      changed = true;
      return { ...session, mediaSettings };
    });
    return changed ? next : sessions;
  };

  const state = useEventStore.getState();
  const eventSessionsPlan = patchList(state.eventSessionsPlan);
  const eventSessionsOrganize = patchList(state.eventSessionsOrganize);

  if (eventSessionsPlan !== state.eventSessionsPlan || eventSessionsOrganize !== state.eventSessionsOrganize) {
    useEventStore.setState({
      ...(eventSessionsPlan !== state.eventSessionsPlan ? { eventSessionsPlan } : {}),
      ...(eventSessionsOrganize !== state.eventSessionsOrganize ? { eventSessionsOrganize } : {}),
    });
  }
}
